import React, { useState, useEffect } from 'react';
import './style/tarification.css';
import { useNavigate } from 'react-router-dom';
import { GoArrowLeft } from 'react-icons/go';
import TypeAnnoncePercentagesService from '../services/TypeAnnoncePercentagesService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useScrapedTarifs } from '../hooks/useScrapedTarifs';

interface TarificationItem {
  type: string;
  nSType?: number;
  annexeI_VI: number;
  base: number;
}

interface AnnexeInfo {
  name: string;
  departments: string[];
  tarif: number;
}

const TarificationPage = () => {
  const navigate = useNavigate();
  const [percentages, setPercentages] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState<boolean>(true);

  // Hook de scraping
  const {
    mappedTarifs,
    loading: scrapingLoading
  } = useScrapedTarifs();

  useEffect(() => {
    const fetchPercentages = async () => {
      try {
        const response = await TypeAnnoncePercentagesService.getPercentages();
        const formattedData = response.data.reduce((acc: any, item: any) => {
          acc[item.nSType] = item;
          return acc;
        }, {});
        setPercentages(formattedData);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la récupération des pourcentages:', err);
        toast.error('Erreur lors de la récupération des pourcentages');
        setLoading(false);
      }
    };
    fetchPercentages();
  }, []);

  const getPercentage = (type: string): number => {
    const percentageItem = Object.values(percentages).find(
      (p) => p.sSType === type
    );
    return percentageItem?.discountPercentage || 0;
  };

  // Générer les données de tarification depuis le mapping
  const generateTarificationData = () => {
    if (!mappedTarifs) {
      return {
        modifications: [],
        constitutions: [],
        dissolutions: [],
        autres: []
      };
    }

    const mapCategory = (items: any[]): TarificationItem[] => {
      return items.map(item => ({
        type: item.type,
        nSType: item.nSType,
        annexeI_VI: item.prixAnnexeI_VI,
        base: item.prixBase
      }));
    };

    return {
      modifications: mapCategory(mappedTarifs.modifications),
      constitutions: mapCategory(mappedTarifs.constitutions),
      dissolutions: mapCategory(mappedTarifs.dissolutions),
      autres: mapCategory(mappedTarifs.autres)
    };
  };

  const tarificationData = generateTarificationData();

  // Générer les annexes depuis les données scrapées
  const generateAnnexesData = (): AnnexeInfo[] => {
    const defaultAnnexes = [
      {
        name: 'Annexe I',
        departments: [
          '01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence',
          '05 Hautes-Alpes', '06 Alpes-Maritimes', '09 Ariège',
          '10 Aube', '11 Aude', '12 Aveyron',
          '13 Bouches-du-Rhône', '14 Calvados', '15 Cantal',
          '16 Charente', '17 Charente-Maritime', '18 Cher',
          '19 Corrèze', '2A Corse-du-Sud', '2B Haute-Corse',
          '21 Côte-d\'Or', '22 Côtes-d\'Armor', '24 Dordogne',
          '23 Creuse', '25 Doubs', '29 Finistère',
          '28 Eure-et-Loire', '30 Gard', '31 Haute-Garonne',
          '32 Gers', '33 Gironde', '34 Hérault',
          '35 Ille-et-Vilaine', '36 Indre', '37 Indre-et-Loire',
          '39 Jura', '40 Landes', '41 Loir-et-Cher',
          '42 Loire', '43 Haute-Loire', '44 Loire-Atlantique',
          '45 Loiret', '46 Lot', '47 Lot-et-Garonne',
          '48 Lozère', '49 Maine-et-Loire', '50 Manche',
          '51 Marne', '52 Haute-Marne', '53 Mayenne'
        ],
        tarif: 0.183
      },
      {
        name: 'Annexe II',
        departments: ['01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      },
      {
        name: 'Annexe III',
        departments: ['01 Ain', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      },
      {
        name: 'Annexe IV',
        departments: ['01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      },
      {
        name: 'Annexe V',
        departments: ['01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      },
      {
        name: 'Annexe VI',
        departments: ['01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      },
      {
        name: 'Annexe VII',
        departments: ['01 Ain', '03 Allier', '04 Alpes-de-Haute-Provence'],
        tarif: 0.189
      }
    ];

    // Utiliser les tarifs caractère depuis le scraping si disponibles
    if (mappedTarifs?.tarifsCaractere) {
      return defaultAnnexes.map(annexe => ({
        ...annexe,
        tarif: mappedTarifs.tarifsCaractere[annexe.name] || annexe.tarif
      }));
    }

    return defaultAnnexes;
  };

  const annexesData = generateAnnexesData();

  const handleGoBack = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "https://annonce-legale-officielle.fr/";
    } else {
      navigate('/home/nouvelle-annonce');
    }
  };

  const renderTarificationTable = (title: string, data: TarificationItem[]) => (
    <table className="tarification-table">
      <thead>
        <tr>
          <th>Type d'annonce</th>
          <th>Forfait HT 2025 Annexe I à VI</th>
          <th>Forfait HT 2025 Annexe VII</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => {
          const pourcentage = getPercentage(item.type);
          const base = item.base;
          const annexeVII = base + pourcentage;
          
          return (
            <tr key={index}>
              <td>{item.type}</td>
              <td>{item.annexeI_VI} €</td>
              <td>
                <div>
                  {Number.isInteger(annexeVII)
                    ? annexeVII.toFixed(0)
                    : annexeVII.toFixed(2)} €
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderAnnexeTable = () => (
    <>
      {annexesData.map((annexe, index) => (
        <table key={index} className="tarification-table">
          <thead>
            <tr className='center'>
              <th colSpan={2}>{annexe.name}</th>
              <th colSpan={2}>
                Tarif HT 2025 par caractère : <strong>{annexe.tarif} €</strong>
              </th>
            </tr>
          </thead>
          <tbody>
            {annexe.departments.reduce<string[][]>((acc, dept, i) => {
              if (i % 4 === 0) acc.push([]);
              acc[acc.length - 1].push(dept);
              return acc;
            }, []).map((group, groupIndex) => (
              <tr key={groupIndex}>
                {group.map((dept, deptIndex) => (
                  <td key={deptIndex}>{dept}</td>
                ))}
                {group.length < 4 && Array(4 - group.length).fill(null).map((_, i) => (
                  <td key={`empty-${i}`}> </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </>
  );

  if (loading || scrapingLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p style={{ color: '#6c757d' }}>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="tarification-container">
      <div className="cont">
        <button className="back-button mb-3" onClick={handleGoBack}>
          <GoArrowLeft className="icon" style={{ marginRight: '10px', fontSize: '20px' }} />
          Précédent
        </button>

        <p className='clent'>Chers clients,</p>
        <p>
          La réforme tarifaire prévue dans l'article 3 de la loi PACTE se poursuit en 2025 avec la mise en place d'un nouveau forfait.
          Pour les types d'annonces concernés par les forfaits, le même tarif s'applique dans tous les départements, à l'exception des
          départements de l'Annexes VII. (Retrouver la liste des annexes en bas de cette page)
        </p>

        <section>
          <h3>Modifications</h3>
          {renderTarificationTable('Modifications', tarificationData.modifications)}
        </section>

        <section>
          <h3>Constitutions</h3>
          {renderTarificationTable('Constitutions', tarificationData.constitutions)}
        </section>

        <section>
          <h3>Dissolutions</h3>
          {renderTarificationTable('Dissolutions', tarificationData.dissolutions)}
        </section>

        <section>
          <h3>Autres</h3>
          {renderTarificationTable('Autres', tarificationData.autres)}
        </section>

        <div className="tarification-header">
          <h5>Annexes / Départements - Tarification au caractère</h5>
        </div>

        <section>
          <h3>Annexes et Départements</h3>
          {renderAnnexeTable()}
        </section>

        <div className='pub'>
          <p>
            Ci-dessous le lien vers la publication de l'arrêté au Journal Officiel :<br />
            <a
              href="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000048680411"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'grey', textDecoration: 'none' }}
            >
              https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000048680411
            </a>
          </p>
          <p>
            Notre Service Annonces Légales se tient à votre disposition pour tout renseignement<br />
            Par téléphone au <a href="tel:0188844844" style={{ color: 'grey', textDecoration: 'none' }}>0188 844 844</a><br />
            Par mail à{' '}
            <a href="mailto:service-clients@annonce.legale-officielle.fr" style={{ color: 'grey', textDecoration: 'none' }}>
              service-clients@annonce.legale-officielle.fr
            </a>
          </p>
          <p style={{ marginTop: '20px' }}>
            R.TERRIER<br />
            Direction commercial
          </p>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default TarificationPage;